import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Printer, ClipboardList, CalendarDays, Pill, AlertTriangle, Sun, Clock, ChevronDown, Utensils, Trees } from 'lucide-react';

const HORSE_COLORS = [
  { bg: '#E9EFE8', border: '#B7CDB8', text: '#1B4332' },
  { bg: '#F9F7F3', border: '#E0D8C8', text: '#5C4A2F' },
  { bg: '#EDE9FE', border: '#C4B5FD', text: '#4C1D95' },
  { bg: '#FEF3C7', border: '#F7D98B', text: '#78350F' },
  { bg: '#DBEAFE', border: '#93C5FD', text: '#1E3A5F' },
  { bg: '#FCE7F3', border: '#F9A8D4', text: '#831843' },
  { bg: '#D1FAE5', border: '#6EE7B7', text: '#064E3B' },
  { bg: '#FEE2E2', border: '#FCA5A5', text: '#7F1D1D' },
];

function getHorseColor(idx) {
  return HORSE_COLORS[idx % HORSE_COLORS.length];
}

function computeAge(dob) {
  if (!dob) return null;
  const birth = new Date(dob + 'T00:00:00');
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  if (today.getMonth() < birth.getMonth() || (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())) age--;
  return Math.max(0, age);
}

export default function BarnBoardPage() {
  const { authHeaders, API } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedHorse, setSelectedHorse] = useState('all');
  const [horses, setHorses] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const endpoint = selectedHorse === 'all' ? '/barn-board' : `/barn-board/${selectedHorse}`;
      const res = await axios.get(`${API}${endpoint}`, authHeaders());
      setData(res.data);
      if (selectedHorse === 'all') setHorses(res.data.horses || []);
    } catch (err) {
      toast.error('Failed to load barn board');
    } finally {
      setLoading(false);
    }
  }, [API, authHeaders, selectedHorse]);

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  const handlePrint = () => window.print();

  const today = new Date();
  const dateStr = today.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  // Build horse color map
  const horseColorMap = {};
  (data?.horses || []).forEach((h, i) => { horseColorMap[h.name] = getHorseColor(i); });

  return (
    <div className="min-h-screen bg-[#F2F7F4] texture-grain">
      <div className="print:hidden"><Navbar /></div>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8 animate-fade-in print:mb-4">
          <div>
            <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[#1B4332]" data-testid="barn-board-title">
              Barn Board
            </h1>
            <p className="text-[#4B6B58] mt-1" data-testid="barn-board-date">{dateStr}</p>
          </div>
          <div className="flex items-center gap-3 print:hidden">
            {/* Horse selector */}
            <div className="relative">
              <select
                value={selectedHorse}
                onChange={e => setSelectedHorse(e.target.value)}
                data-testid="barn-board-horse-selector"
                className="stable-input pl-4 pr-10 py-2.5 rounded-full appearance-none text-sm font-medium bg-white"
              >
                <option value="all">All Horses (Barn-Wide)</option>
                {horses.map(h => (
                  <option key={h.id} value={h.id}>{h.name}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4B6B58] pointer-events-none" />
            </div>
            <button
              onClick={handlePrint}
              data-testid="barn-board-print-button"
              className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1B4332] text-white text-sm font-semibold btn-lift shadow-[0_4px_12px_rgb(27,67,50,0.15)] hover:bg-[#2D6A4F]"
            >
              <Printer size={16} /> Print Barn Board
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-[#4B6B58] animate-pulse py-12">Loading barn board...</div>
        ) : !data ? (
          <div className="text-center text-[#4B6B58] py-12">No data available.</div>
        ) : (
          <div className="space-y-6 print:space-y-4">
            {/* Horse Quick Status */}
            <section className="bg-white rounded-[2rem] p-6 shadow-[0_4px_12px_rgb(27,67,50,0.04)] print:rounded-none print:shadow-none print:border print:border-gray-300" data-testid="barn-board-horse-status">
              <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-4 flex items-center gap-2">
                <Sun size={18} className="text-[#8B6914]" /> Horse Status
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="horse-status-table">
                  <thead>
                    <tr className="border-b border-[#D1E0D5]">
                      <th className="text-left py-2 px-3 font-semibold text-[#1B4332]">Horse</th>
                      <th className="text-left py-2 px-3 font-semibold text-[#1B4332]">Breed</th>
                      <th className="text-left py-2 px-3 font-semibold text-[#1B4332]">Age</th>
                      <th className="text-left py-2 px-3 font-semibold text-[#1B4332]">Gender</th>
                      <th className="text-left py-2 px-3 font-semibold text-[#1B4332]">Turnout</th>
                      <th className="text-left py-2 px-3 font-semibold text-[#1B4332]">Alerts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.horses || []).map((horse, idx) => {
                      const c = getHorseColor(idx);
                      return (
                        <tr key={horse.id} style={{ backgroundColor: c.bg }} className="border-b border-white/50 print:border-gray-200" data-testid={`horse-status-row-${horse.id}`}>
                          <td className="py-2.5 px-3 font-semibold" style={{ color: c.text }}>{horse.name}</td>
                          <td className="py-2.5 px-3 text-[#4B6B58]">{horse.breed || '—'}</td>
                          <td className="py-2.5 px-3 text-[#4B6B58]">{horse.date_of_birth ? `${computeAge(horse.date_of_birth)} yrs` : '—'}</td>
                          <td className="py-2.5 px-3 text-[#4B6B58]">{horse.gender || '—'}</td>
                          <td className="py-2.5 px-3 text-[#4B6B58]">{horse.turnout_schedule || '—'}</td>
                          <td className="py-2.5 px-3">
                            {horse.special_alerts ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E] text-xs font-medium">
                                <AlertTriangle size={10} /> {horse.special_alerts}
                              </span>
                            ) : (
                              <span className="text-[#2D6A4F] text-xs">All good</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Feed Schedules */}
            <section className="bg-white rounded-[2rem] p-6 shadow-[0_4px_12px_rgb(27,67,50,0.04)] print:rounded-none print:shadow-none print:border print:border-gray-300" data-testid="barn-board-feed">
              <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-4 flex items-center gap-2">
                <Utensils size={18} className="text-[#8B6914]" /> Feed Schedules
              </h2>
              {(data.horses || []).some(h => h.feed_schedule?.length > 0) ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="feed-table">
                    <thead>
                      <tr className="border-b border-[#D1E0D5]">
                        <th className="text-left py-2 px-3 font-semibold text-[#1B4332]">Horse</th>
                        <th className="text-left py-2 px-3 font-semibold text-[#1B4332]">Time</th>
                        <th className="text-left py-2 px-3 font-semibold text-[#1B4332]">Feed Type</th>
                        <th className="text-left py-2 px-3 font-semibold text-[#1B4332]">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.horses || []).flatMap((horse, hIdx) =>
                        (horse.feed_schedule || []).map((feed, fIdx) => {
                          const c = getHorseColor(hIdx);
                          return (
                            <tr key={`${horse.id}-${fIdx}`} style={{ backgroundColor: c.bg }} className="border-b border-white/50 print:border-gray-200">
                              <td className="py-2 px-3 font-semibold" style={{ color: c.text }}>{horse.name}</td>
                              <td className="py-2 px-3 text-[#4B6B58]">{feed.time || '—'}</td>
                              <td className="py-2 px-3 text-[#4B6B58]">{feed.feed_type || '—'}</td>
                              <td className="py-2 px-3 text-[#4B6B58]">{feed.amount || '—'}</td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-[#4B6B58]">No feed schedules set. Edit horse profiles to add feed information.</p>
              )}
            </section>

            <div className="grid md:grid-cols-2 gap-6 print:gap-4">
              {/* Today's Chores */}
              <section className="bg-white rounded-[2rem] p-6 shadow-[0_4px_12px_rgb(27,67,50,0.04)] print:rounded-none print:shadow-none print:border print:border-gray-300" data-testid="barn-board-chores">
                <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-4 flex items-center gap-2">
                  <ClipboardList size={18} className="text-[#2D6A4F]" /> Today's Chores
                </h2>
                {(data.todays_chores || []).length > 0 ? (
                  <ul className="space-y-2">
                    {data.todays_chores.map((chore, i) => (
                      <li key={chore.id || i} className={`flex items-start gap-3 p-3 rounded-xl ${i % 2 === 0 ? 'bg-[#F9F7F3]' : 'bg-[#E9EFE8]'}`} data-testid={`chore-item-${i}`}>
                        <div className={`w-5 h-5 rounded border-2 flex-shrink-0 mt-0.5 ${chore.completed ? 'bg-[#2D6A4F] border-[#2D6A4F]' : 'border-[#D1E0D5]'}`} />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${chore.completed ? 'line-through text-[#4B6B58]' : 'text-[#122A1F]'}`}>{chore.title}</p>
                          {chore.assigned_to && <p className="text-xs text-[#4B6B58]">Assigned: {chore.assigned_to}</p>}
                          {chore.frequency && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#1B4332]/10 text-[#2D6A4F] font-medium">{chore.frequency}</span>}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-[#4B6B58]">No chores scheduled for today.</p>
                )}
              </section>

              {/* Upcoming Appointments */}
              <section className="bg-white rounded-[2rem] p-6 shadow-[0_4px_12px_rgb(27,67,50,0.04)] print:rounded-none print:shadow-none print:border print:border-gray-300" data-testid="barn-board-appointments">
                <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-4 flex items-center gap-2">
                  <CalendarDays size={18} className="text-[#1D4ED8]" /> Upcoming (Next 7 Days)
                </h2>
                {(data.upcoming_appointments || []).length > 0 ? (
                  <ul className="space-y-2">
                    {data.upcoming_appointments.map((apt, i) => {
                      const c = horseColorMap[apt.horse] || getHorseColor(i);
                      return (
                        <li key={i} style={{ backgroundColor: c.bg }} className="flex items-start gap-3 p-3 rounded-xl" data-testid={`appointment-item-${i}`}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-white/60" style={{ color: c.text }}>{apt.horse}</span>
                              <span className="text-xs text-[#4B6B58]">{new Date(apt.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                            </div>
                            <p className="text-sm font-medium text-[#122A1F]">{apt.type}: {apt.detail}</p>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="text-sm text-[#4B6B58]">No upcoming appointments this week.</p>
                )}
              </section>
            </div>

            {/* Medication Schedules */}
            <section className="bg-white rounded-[2rem] p-6 shadow-[0_4px_12px_rgb(27,67,50,0.04)] print:rounded-none print:shadow-none print:border print:border-gray-300" data-testid="barn-board-medications">
              <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-4 flex items-center gap-2">
                <Pill size={18} className="text-[#7C3AED]" /> Medication Schedules
              </h2>
              {(data.medications || []).length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="medications-table">
                    <thead>
                      <tr className="border-b border-[#D1E0D5]">
                        <th className="text-left py-2 px-3 font-semibold text-[#1B4332]">Horse</th>
                        <th className="text-left py-2 px-3 font-semibold text-[#1B4332]">Medication</th>
                        <th className="text-left py-2 px-3 font-semibold text-[#1B4332]">Dosage</th>
                        <th className="text-left py-2 px-3 font-semibold text-[#1B4332]">Time</th>
                        <th className="text-left py-2 px-3 font-semibold text-[#1B4332]">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.medications.map((med, i) => {
                        const c = horseColorMap[med.horse] || getHorseColor(i);
                        return (
                          <tr key={i} style={{ backgroundColor: c.bg }} className="border-b border-white/50 print:border-gray-200" data-testid={`med-row-${i}`}>
                            <td className="py-2 px-3 font-semibold" style={{ color: c.text }}>{med.horse}</td>
                            <td className="py-2 px-3 text-[#122A1F]">{med.medication}</td>
                            <td className="py-2 px-3 text-[#4B6B58]">{med.dosage || '—'}</td>
                            <td className="py-2 px-3 text-[#4B6B58]">{med.time || '—'}</td>
                            <td className="py-2 px-3 text-[#4B6B58] max-w-[200px] truncate">{med.notes || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-[#4B6B58]">No active medications recorded.</p>
              )}
            </section>

            {/* Turnout Rotation */}
            {(data.horses || []).some(h => h.turnout_schedule) && (
              <section className="bg-white rounded-[2rem] p-6 shadow-[0_4px_12px_rgb(27,67,50,0.04)] print:rounded-none print:shadow-none print:border print:border-gray-300" data-testid="barn-board-turnout">
                <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-4 flex items-center gap-2">
                  <Trees size={18} className="text-[#2D6A4F]" /> Turnout Rotation
                </h2>
                <div className="space-y-2">
                  {(data.horses || []).filter(h => h.turnout_schedule).map((horse, idx) => {
                    const c = getHorseColor(idx);
                    return (
                      <div key={horse.id} style={{ backgroundColor: c.bg }} className="flex items-center gap-3 p-3 rounded-xl" data-testid={`turnout-${horse.id}`}>
                        <span className="font-semibold text-sm" style={{ color: c.text }}>{horse.name}</span>
                        <span className="text-sm text-[#4B6B58]">{horse.turnout_schedule}</span>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Print footer */}
            <div className="hidden print:block text-center text-xs text-gray-400 mt-4 pt-4 border-t border-gray-200">
              Stable Solutions Barn Board — Printed {dateStr}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
